import React, { ReactElement, useState } from "react";
import {
	Alert,
	Button,
	Card,
	Form,
	FormControl,
	FormGroup,
	FormLabel,
	InputGroup,
} from "react-bootstrap";
import { toast } from "react-toastify";

import { useProjectBudgetContext } from "./ProjectBudgetContext";

/**
 * Admin controls for the client facing hours summary: the shared link and the
 * recipients of the weekly email.
 */
export const ProjectBudgetReportCard = (): ReactElement | null => {
	const projectBudgetContext = useProjectBudgetContext();
	const budget = projectBudgetContext.budget;

	const [working, setWorking] = useState(false);

	if (!budget) {
		return null;
	}

	const hasRate = !!budget.hourlyRate && budget.hourlyRate > 0;
	const recipients = budget.reportRecipients || [];

	// Amounts are stored in USD, so a CAD report also needs an exchange rate.
	const isCad = budget.reportCurrency === "CAD";
	const hasExchangeRate =
		!isCad || (!!budget.exchangeRate && budget.exchangeRate > 0);
	const isReady = hasRate && hasExchangeRate;

	const money = (value: number, currency: string) =>
		`$${value.toLocaleString(undefined, {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})} ${currency}`;

	// Fields save when they lose focus. Comparing against what is already on the
	// budget avoids a pointless round trip when someone tabs through untouched.
	const saveIfChanged = (
		value: string,
		current: string,
		apply: (value: string) => void,
	) => {
		if (value.trim() === current.trim()) {
			return;
		}

		apply(value.trim());
		projectBudgetContext.save!();
	};

	// Blank clears the field; anything unparseable is ignored rather than
	// silently stored as NaN.
	const toNumber = (value: string): number | null => {
		if (value === "") {
			return null;
		}

		const parsed = parseFloat(value);

		return isNaN(parsed) ? null : parsed;
	};

	const copyLink = () => {
		if (!budget.reportUrl) {
			return;
		}

		navigator.clipboard
			.writeText(budget.reportUrl)
			.then(() => toast.success("Link copied to the clipboard"))
			.catch(() => toast.error("Could not copy the link"));
	};

	const createLink = async () => {
		setWorking(true);
		try {
			await projectBudgetContext.createReportLink!();
			toast.success("Shareable link created");
		} catch (error) {
			toast.error((error as Error).message);
		} finally {
			setWorking(false);
		}
	};

	const revokeLink = async () => {
		setWorking(true);
		try {
			await projectBudgetContext.revokeReportLink!();
			toast.success("Link revoked. The old URL no longer works.");
		} catch (error) {
			toast.error((error as Error).message);
		} finally {
			setWorking(false);
		}
	};

	const linkSection = budget.reportUrl ? (
		<>
			<InputGroup className="mb-2">
				<FormControl readOnly value={budget.reportUrl} />
				<Button variant="outline-secondary" onClick={copyLink}>
					Copy
				</Button>
				<Button
					variant="outline-secondary"
					href={budget.reportUrl}
					target="_blank"
					rel="noopener noreferrer"
				>
					Preview
				</Button>
			</InputGroup>
			<Button
				variant="link"
				className="p-0"
				disabled={working}
				onClick={revokeLink}
			>
				Revoke this link
			</Button>
			<Button
				variant="link"
				className="p-0 ms-3"
				disabled={working}
				onClick={createLink}
			>
				Generate a new link
			</Button>
		</>
	) : (
		<Button disabled={working || !isReady} onClick={createLink}>
			Create shareable link
		</Button>
	);

	const currencyField = (
		<Form.Select
			value={budget.reportCurrency || "USD"}
			style={{ maxWidth: 220 }}
			onChange={(event: any) => {
				budget.reportCurrency = event.target.value;
				projectBudgetContext.save!();
			}}
		>
			<option value="USD">USD &mdash; as stored</option>
			<option value="CAD">CAD &mdash; converted</option>
		</Form.Select>
	);

	const exchangeRateField = (
		<>
			<FormControl
				type="text"
				style={{ maxWidth: 220 }}
				defaultValue={
					budget.exchangeRate ? String(budget.exchangeRate) : ""
				}
				placeholder={document.body.getAttribute("data-exr") || "1.36"}
				onBlur={(event: any) =>
					saveIfChanged(
						event.target.value,
						budget.exchangeRate ? String(budget.exchangeRate) : "",
						value => {
							budget.exchangeRate = toNumber(value);
						},
					)
				}
			/>
			<Form.Text className="text-muted">
				The rate used for this budget, as $1.00 USD = $x CAD.
			</Form.Text>
		</>
	);

	const hourlyRateField = (
		<>
			<InputGroup style={{ maxWidth: 220 }}>
				<InputGroup.Text>$</InputGroup.Text>
				<FormControl
					type="text"
					defaultValue={
						budget.hourlyRate ? String(budget.hourlyRate) : ""
					}
					onBlur={(event: any) =>
						saveIfChanged(
							event.target.value,
							budget.hourlyRate ? String(budget.hourlyRate) : "",
							value => {
								budget.hourlyRate = toNumber(value);
							},
						)
					}
				/>
				<InputGroup.Text>
					{budget.reportCurrency || "USD"} / hr
				</InputGroup.Text>
			</InputGroup>
			<Form.Text className="text-muted">
				Entered in {budget.reportCurrency || "USD"}, the same currency
				the report is shown in.
			</Form.Text>
		</>
	);

	// Spelling the arithmetic out makes a mistyped rate obvious straight away.
	const derivation = isReady ? (
		<Alert variant="light" className="mb-0 border">
			{money(Number(budget.amount), "USD")}
			{isCad ? ` × ${budget.exchangeRate} = ` : " = "}
			{isCad
				? money(
						Number(budget.reportAmount),
						budget.reportCurrency || "USD",
				  )
				: null}
			{" ÷ "}
			{money(Number(budget.hourlyRate), budget.reportCurrency || "USD")}
			{" / hr = "}
			<strong>
				{Number(budget.hoursTotal).toLocaleString(undefined, {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				})}{" "}
				hours
			</strong>
		</Alert>
	) : null;

	// Budget names are internal and often contain dollar figures, so the client
	// facing heading can be overridden here.
	const titleField = (
		<>
			<FormControl
				type="text"
				defaultValue={budget.reportTitle || ""}
				placeholder={budget.name}
				onBlur={(event: any) =>
					saveIfChanged(
						event.target.value,
						budget.reportTitle || "",
						value => {
							budget.reportTitle = value === "" ? null : value;
						},
					)
				}
			/>
			<Form.Text className="text-muted">
				Shown as the heading on the report. Leave blank to use the
				budget name.
			</Form.Text>
		</>
	);

	const recipientsField = (
		<FormControl
			as="textarea"
			rows={3}
			defaultValue={recipients.join("\n")}
			placeholder="One email address per line"
			onBlur={(event: any) =>
				saveIfChanged(event.target.value, recipients.join("\n"), value => {
					budget.reportRecipients = value
						.split(/[\s,;]+/)
						.map((email: string) => email.trim())
						.filter((email: string) => email.length > 0);
				})
			}
		/>
	);

	return (
		<Card className="mb-4">
			<Card.Body>
				<Card.Title>Client Hours Report</Card.Title>
				<p>
					A public page showing the hours available on this budget,
					the hours used per project and each project&rsquo;s status.
					Anyone with the link can view it, and no dollar values or
					logged time are shown.
				</p>

				{!hasRate ? (
					<Alert variant="warning">
						Set an <strong>hourly rate</strong> below before sharing
						a report &mdash; it is what converts the budget into
						hours.
					</Alert>
				) : null}

				{hasRate && !hasExchangeRate ? (
					<Alert variant="warning">
						This report is in CAD, so an{" "}
						<strong>exchange rate</strong> is needed to convert the
						budget from the USD it is stored in.
					</Alert>
				) : null}

				<FormGroup className="mb-4">
					<FormLabel>
						<strong>Report currency</strong>
					</FormLabel>
					{currencyField}
					<Form.Text className="text-muted">
						Budget amounts are stored in USD. Choose CAD if this
						client was quoted in Canadian dollars.
					</Form.Text>
				</FormGroup>

				{isCad ? (
					<FormGroup className="mb-4">
						<FormLabel>
							<strong>Exchange rate</strong>{" "}
							<span className="text-muted">
								($1.00 USD = $x CAD)
							</span>
						</FormLabel>
						{exchangeRateField}
					</FormGroup>
				) : null}

				<FormGroup className="mb-4">
					<FormLabel>
						<strong>Hourly rate</strong>{" "}
						<span className="text-muted">
							(in {budget.reportCurrency || "USD"})
						</span>
					</FormLabel>
					{hourlyRateField}
				</FormGroup>

				{derivation ? (
					<FormGroup className="mb-4">{derivation}</FormGroup>
				) : null}

				<FormGroup className="mb-4">
					<FormLabel>
						<strong>Report title</strong>
					</FormLabel>
					{titleField}
				</FormGroup>

				<FormGroup>
					<FormLabel>
						<strong>Shareable link</strong>
					</FormLabel>
					<div>{linkSection}</div>
				</FormGroup>

				<FormGroup className="mt-4 mb-0">
					<FormLabel>
						<strong>Weekly email recipients</strong>
					</FormLabel>
					{recipientsField}
					{budget.reportLastSentAt ? (
						<Form.Text className="text-muted">
							Last sent{" "}
							{new Date(
								budget.reportLastSentAt,
							).toLocaleDateString()}
							.
						</Form.Text>
					) : (
						<Form.Text className="text-muted">
							Sent every Monday morning while a link is active.
						</Form.Text>
					)}
				</FormGroup>
			</Card.Body>
		</Card>
	);
};
